import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Building2, Package, DollarSign, TrendingUp, ArrowUpRight, Target, Pin, Star, Save, X, Activity } from "lucide-react";
import { Loader } from "@/components/Loader";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import VelocityWidget from "@/components/VelocityWidget";
import ThemeStatsWidget from "@/components/ThemeStatsWidget";
import ReplyStatsWidget from "@/components/ReplyStatsWidget";
import TickerBar, { AnimatedNumber } from "@/components/TickerBar";
import EmptyDashboard from "@/components/EmptyDashboard";

const fmtMoney = (n) => {
    if (!n) return "$0";
    if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
    if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
    if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
    return `$${n.toFixed(0)}`;
};

const PIE_AMBER = ["#F59E0B", "#D97706", "#92400E", "#FCD34D", "#FBBF24", "#B45309", "#78350F", "#FDE68A"];
const REGION_COLORS = {
    EU: "#3B82F6",      // blue
    US: "#F59E0B",      // amber
    Asia: "#10B981",    // emerald
    LATAM: "#EC4899",   // pink
    MEA: "#A855F7",     // purple
    Global: "#06B6D4",  // cyan
    Other: "#71717A",   // zinc
};

function RevenuePie({ data, colorMap, fallbackColors, testid, onSliceClick, sliceKind }) {
    if (!data || data.length === 0) {
        return <div className="text-xs text-zinc-600 font-mono py-12 text-center">No revenue data yet</div>;
    }
    return (
        <div style={{ width: "100%", height: 240, minWidth: 0 }} data-testid={testid}>
            <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                    <Pie
                        data={data}
                        dataKey="revenue"
                        nameKey="name"
                        innerRadius={45}
                        outerRadius={80}
                        stroke="#000"
                        strokeWidth={1}
                        paddingAngle={1}
                        onClick={(slice) => onSliceClick && onSliceClick(slice?.name, sliceKind)}
                        cursor={onSliceClick ? "pointer" : "default"}
                    >
                        {data.map((d, i) => (
                            <Cell key={i} fill={(colorMap && colorMap[d.name]) || fallbackColors[i % fallbackColors.length]} data-testid={`${testid}-slice-${d.name}`} />
                        ))}
                    </Pie>
                    <Tooltip
                        contentStyle={{ background: "#09090B", border: "1px solid #27272A", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11, color: "#fafafa" }}
                        formatter={(v) => fmtMoney(v)}
                    />
                    <Legend
                        wrapperStyle={{ fontFamily: "JetBrains Mono", fontSize: 10, paddingTop: 8, cursor: onSliceClick ? "pointer" : "default" }}
                        iconType="square"
                        formatter={(value) => <span style={{ color: "#fafafa" }}>{value}</span>}
                        onClick={(entry) => onSliceClick && onSliceClick(entry?.value, sliceKind)}
                    />
                </PieChart>
            </ResponsiveContainer>
        </div>
    );
}

const StatCard = ({ label, value, displayValue, icon: Icon, accent, testid, glow }) => (
    <div className={`border ${accent ? "border-amber-500/30" : "border-zinc-800"} bg-zinc-950 p-5 relative ${glow ? "kpi-glow" : ""}`} data-testid={testid}>
        <div className="flex items-center justify-between mb-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</div>
            <Icon className={`w-4 h-4 ${accent ? "text-amber-500" : "text-zinc-600"}`} />
        </div>
        <div className="font-heading font-black tracking-tighter text-3xl text-zinc-50">
            {displayValue !== undefined ? displayValue : value}
        </div>
    </div>
);

export default function Dashboard() {
    const nav = useNavigate();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [range, setRange] = useState("all");
    const [region, setRegion] = useState("All");
    const [industry, setIndustry] = useState("All");
    const [firmsForOpts, setFirmsForOpts] = useState([]);
    const [presets, setPresets] = useState([]);
    const [presetName, setPresetName] = useState("");
    const [comparePresetId, setComparePresetId] = useState("");
    const [compareStats, setCompareStats] = useState(null);

    // Load firms once for region/industry option lists
    useEffect(() => { api.get("/firms").then(({ data }) => setFirmsForOpts(data || [])).catch(() => {}); }, []);
    useEffect(() => { api.get("/presets").then(({ data }) => setPresets(data || [])).catch(() => {}); }, []);

    useEffect(() => {
        setLoading(true);
        const params = new URLSearchParams({ range });
        if (region !== "All") params.set("region", region);
        if (industry !== "All") params.set("industry", industry);
        api.get(`/stats?${params}`)
            .then(({ data }) => setStats(data))
            .finally(() => setLoading(false));
    }, [range, region, industry]);

    const regionOpts = useMemo(() => ["All", ...Array.from(new Set(firmsForOpts.map((f) => f.region).filter(Boolean)))], [firmsForOpts]);
    const industryOpts = useMemo(() => ["All", ...Array.from(new Set(firmsForOpts.map((f) => f.industry).filter(Boolean)))], [firmsForOpts]);
    const firmColor = useMemo(() => Object.fromEntries(firmsForOpts.map((f) => [f.id, f.color]).filter(([, c]) => c)), [firmsForOpts]);

    const onSliceClick = (name, kind) => {
        if (!name) return;
        const params = new URLSearchParams();
        if (kind === "region") params.set("region", name);
        if (kind === "industry") params.set("industry", name);
        nav(`/app/library?${params.toString()}`);
    };

    const savePreset = async () => {
        if (!presetName.trim()) return;
        const { data } = await api.post("/presets", { name: presetName.trim(), range, region, industry });
        setPresets((p) => [data, ...p]);
        setPresetName("");
    };

    const loadPreset = (p) => {
        setRange(p.range || "all");
        setRegion(p.region || "All");
        setIndustry(p.industry || "All");
    };

    const deletePreset = async (id, e) => {
        e?.stopPropagation();
        if (!window.confirm("Delete this preset?")) return;
        await api.delete(`/presets/${id}`);
        setPresets((arr) => arr.filter((x) => x.id !== id));
        if (comparePresetId === id) { setComparePresetId(""); setCompareStats(null); }
    };

    // Compare preset stats
    useEffect(() => {
        if (!comparePresetId) { setCompareStats(null); return; }
        const p = presets.find((x) => x.id === comparePresetId);
        if (!p) return;
        const params = new URLSearchParams({ range: p.range || "all" });
        if (p.region && p.region !== "All") params.set("region", p.region);
        if (p.industry && p.industry !== "All") params.set("industry", p.industry);
        api.get(`/stats?${params}`).then(({ data }) => setCompareStats(data)).catch(() => setCompareStats(null));
    }, [comparePresetId, presets]);

    const comparePreset = useMemo(() => presets.find((p) => p.id === comparePresetId), [presets, comparePresetId]);

    // Reload everything after demo seeding so the dashboard repaints.
    const reloadAll = () => {
        setLoading(true);
        api.get("/firms").then(({ data }) => setFirmsForOpts(data || [])).catch(() => {});
        const params = new URLSearchParams({ range });
        if (region !== "All") params.set("region", region);
        if (industry !== "All") params.set("industry", industry);
        api.get(`/stats?${params}`).then(({ data }) => setStats(data)).finally(() => setLoading(false));
    };

    if (loading) return <div className="p-12"><Loader label="Loading dashboard" /></div>;

    // EMPTY STATE — admin has no firms yet. Surface the illustrated empty-state with seed CTA.
    const isEmpty = (stats?.total_firms || 0) === 0 && (stats?.total_synergies || 0) === 0;
    if (isEmpty) {
        return (
            <div className="p-8" data-testid="dashboard-page">
                <header className="mb-8 flex items-end justify-between border-b border-zinc-800 pb-6 gap-4 flex-wrap">
                    <div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Overview</div>
                        <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Dashboard</h1>
                        <p className="text-sm text-zinc-500 mt-2">Welcome — let's get your workspace populated.</p>
                    </div>
                </header>
                <EmptyDashboard onSeeded={reloadAll} />
            </div>
        );
    }

    const RANGES = [
        { v: "all", l: "All time" },
        { v: "12m", l: "12 months" },
        { v: "6m", l: "6 months" },
        { v: "30d", l: "30 days" },
    ];

    return (
        <div className="p-8" data-testid="dashboard-page">
            <header className="mb-8 flex items-end justify-between border-b border-zinc-800 pb-6 gap-4 flex-wrap">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-amber-500 mb-2">// Overview</div>
                    <h1 className="font-heading font-black tracking-tighter text-4xl text-zinc-50">Dashboard</h1>
                    <p className="text-sm text-zinc-500 mt-2">Snapshot of your research workspace.</p>
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-1" data-testid="range-toggle">
                        {RANGES.map((r) => (
                            <button
                                key={r.v}
                                data-testid={`range-${r.v}`}
                                onClick={() => setRange(r.v)}
                                className={`text-xs font-mono uppercase tracking-wider px-2.5 py-2 border transition-colors ${
                                    range === r.v
                                        ? "border-amber-500 bg-amber-500/10 text-amber-400"
                                        : "border-zinc-800 text-zinc-400 hover:border-zinc-600"
                                }`}
                            >
                                {r.l}
                            </button>
                        ))}
                    </div>
                    <Link to="/app/research" data-testid="dashboard-new-research-btn" className="bg-amber-500 hover:bg-amber-400 text-black font-semibold text-xs uppercase tracking-wider px-4 py-2.5 flex items-center gap-2">
                        New research <ArrowUpRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </header>

            {/* Filters + Presets bar */}
            <div className="border border-zinc-800 bg-zinc-950 p-3 mb-4 flex items-center gap-3 flex-wrap" data-testid="dashboard-filters">
                <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Region</span>
                    <select
                        data-testid="dash-region-select"
                        value={region}
                        onChange={(e) => setRegion(e.target.value)}
                        className="bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 py-1 focus:outline-none focus:border-amber-500 font-mono"
                    >
                        {regionOpts.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>
                <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Industry</span>
                    <select
                        data-testid="dash-industry-select"
                        value={industry}
                        onChange={(e) => setIndustry(e.target.value)}
                        className="bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 py-1 focus:outline-none focus:border-amber-500 font-mono max-w-[200px]"
                    >
                        {industryOpts.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>

                <div className="h-5 w-px bg-zinc-800" />

                <div className="flex items-center gap-2">
                    <input
                        value={presetName}
                        onChange={(e) => setPresetName(e.target.value)}
                        placeholder="Preset name…"
                        data-testid="preset-name-input"
                        className="bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 py-1 focus:outline-none focus:border-amber-500 font-mono w-40"
                    />
                    <button
                        onClick={savePreset}
                        disabled={!presetName.trim()}
                        data-testid="save-preset-btn"
                        className="inline-flex items-center gap-1.5 border border-zinc-800 hover:border-amber-500 hover:text-amber-500 text-xs font-mono uppercase tracking-wider text-zinc-400 px-2 py-1 transition-colors disabled:opacity-40"
                    >
                        <Save className="w-3 h-3" /> Save
                    </button>
                </div>

                {presets.length > 0 && (
                    <div className="flex items-center gap-1.5 flex-wrap ml-auto" data-testid="preset-list">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Presets:</span>
                        {presets.map((p) => (
                            <button
                                key={p.id}
                                onClick={() => loadPreset(p)}
                                data-testid={`preset-${p.id}`}
                                className="inline-flex items-center gap-1 border border-zinc-800 hover:border-amber-500 text-xs font-mono text-zinc-300 hover:text-amber-400 px-2 py-1 transition-colors group"
                            >
                                {p.name}
                                <X className="w-3 h-3 opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400" onClick={(e) => deletePreset(p.id, e)} />
                            </button>
                        ))}
                        <div className="h-5 w-px bg-zinc-800 mx-1" />
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Compare:</span>
                        <select
                            data-testid="compare-preset-select"
                            value={comparePresetId}
                            onChange={(e) => setComparePresetId(e.target.value)}
                            className="bg-zinc-950 border border-zinc-800 text-xs text-zinc-200 px-2 py-1 focus:outline-none focus:border-amber-500 font-mono"
                        >
                            <option value="">— off —</option>
                            {presets.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                        </select>
                    </div>
                )}
            </div>

            {/* Bloomberg-style live ticker — workspace heartbeat */}
            <TickerBar />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard
                    label="Firms tracked" icon={Building2} accent glow testid="stat-firms"
                    value={stats?.total_firms || 0}
                    displayValue={<AnimatedNumber value={stats?.total_firms || 0} testId="stat-firms-num" />}
                />
                <StatCard
                    label="Products mapped" icon={Package} testid="stat-products"
                    value={stats?.total_products || 0}
                    displayValue={<AnimatedNumber value={stats?.total_products || 0} testId="stat-products-num" />}
                />
                <StatCard
                    label="Tracked revenue" icon={DollarSign} testid="stat-revenue"
                    value={fmtMoney(stats?.total_revenue || 0)}
                    displayValue={<AnimatedNumber value={stats?.total_revenue || 0} formatter={fmtMoney} testId="stat-revenue-num" />}
                />
                <StatCard
                    label="Synergies saved" icon={Target} testid="stat-synergies"
                    value={stats?.total_synergies || 0}
                    displayValue={<AnimatedNumber value={stats?.total_synergies || 0} testId="stat-synergies-num" />}
                />
            </div>

            {/* Revenue pies */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
                <div className="border border-zinc-800 bg-zinc-950" data-testid="revenue-by-region-card">
                    <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-amber-500" />
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Revenue by region</div>
                        </div>
                        <div className="font-mono text-[10px] text-zinc-700">$ MIX</div>
                    </div>
                    <div className="p-4">
                        <RevenuePie data={stats?.revenue_by_region || []} colorMap={REGION_COLORS} fallbackColors={PIE_AMBER} testid="revenue-by-region-pie" onSliceClick={onSliceClick} sliceKind="region" />
                    </div>
                </div>
                <div className="border border-zinc-800 bg-zinc-950" data-testid="revenue-by-industry-card">
                    <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <TrendingUp className="w-4 h-4 text-amber-500" />
                            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Revenue by industry</div>
                        </div>
                        <div className="font-mono text-[10px] text-zinc-700">$ MIX</div>
                    </div>
                    <div className="p-4">
                        <RevenuePie data={stats?.revenue_by_industry || []} fallbackColors={PIE_AMBER} testid="revenue-by-industry-pie" onSliceClick={onSliceClick} sliceKind="industry" />
                    </div>
                </div>
            </div>

            {/* Compare row — second set of pies for the selected preset */}
            {compareStats && comparePreset && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4 animate-fade-in" data-testid="compare-row">
                    <div className="border border-amber-500/30 bg-zinc-950">
                        <div className="px-5 py-4 border-b border-amber-500/20 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <DollarSign className="w-4 h-4 text-amber-500" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400">Revenue by region · {comparePreset.name}</div>
                            </div>
                            <div className="font-mono text-[10px] text-amber-500/70">{fmtMoney(compareStats.total_revenue)}</div>
                        </div>
                        <div className="p-4">
                            <RevenuePie data={compareStats?.revenue_by_region || []} colorMap={REGION_COLORS} fallbackColors={PIE_AMBER} testid="compare-region-pie" />
                        </div>
                    </div>
                    <div className="border border-amber-500/30 bg-zinc-950">
                        <div className="px-5 py-4 border-b border-amber-500/20 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <TrendingUp className="w-4 h-4 text-amber-500" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-400">Revenue by industry · {comparePreset.name}</div>
                            </div>
                            <div className="font-mono text-[10px] text-amber-500/70">{compareStats.total_firms} firm{compareStats.total_firms === 1 ? "" : "s"}</div>
                        </div>
                        <div className="p-4">
                            <RevenuePie data={compareStats?.revenue_by_industry || []} fallbackColors={PIE_AMBER} testid="compare-industry-pie" />
                        </div>
                    </div>
                </div>
            )}

            {/* Compare diff strip */}
            {compareStats && comparePreset && (
                <div className="border border-zinc-800 bg-zinc-950 p-4 mb-4 flex items-center gap-6 flex-wrap font-mono text-xs" data-testid="compare-diff">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Diff (current − {comparePreset.name}):</span>
                    {[
                        { label: "Firms", a: stats?.total_firms || 0, b: compareStats.total_firms || 0 },
                        { label: "Products", a: stats?.total_products || 0, b: compareStats.total_products || 0 },
                        { label: "Revenue", a: stats?.total_revenue || 0, b: compareStats.total_revenue || 0, money: true },
                    ].map(({ label, a, b, money }) => {
                        const d = a - b;
                        const tone = d > 0 ? "text-emerald-400" : d < 0 ? "text-red-400" : "text-zinc-400";
                        return (
                            <span key={label} className="inline-flex items-center gap-1.5">
                                <span className="text-zinc-500 uppercase tracking-wider text-[10px]">{label}</span>
                                <span className={tone}>{d >= 0 ? "+" : ""}{money ? fmtMoney(d) : d}</span>
                            </span>
                        );
                    })}
                </div>
            )}

            {/* Pipeline velocity */}
            <VelocityWidget />

            {/* Per-pitch reply funnel — Pitched / Replied / Reply rate */}
            <div className="mb-4">
                <ReplyStatsWidget />
            </div>

            {/* Pipeline value by theme cluster */}
            <ThemeStatsWidget />

            {/* Top Opportunities */}
            <div className="border border-zinc-800 bg-zinc-950 mb-4" data-testid="top-opportunities-panel">
                <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-amber-500" />
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-amber-500">Top opportunities</div>
                    </div>
                    <div className="font-mono text-[10px] text-zinc-700">BY PROJECTED $</div>
                </div>
                {(stats?.top_opportunities || []).length === 0 ? (
                    <div className="p-6 text-sm text-zinc-500">
                        No saved synergies yet. Go to <Link to="/app/synergy" className="text-amber-500 hover:text-amber-400">Synergy</Link> to score firm pairs.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid="top-opportunities-table">
                            <thead>
                                <tr className="border-b border-zinc-800 text-left">
                                    <th className="px-5 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">#</th>
                                    <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Source · Product</th>
                                    <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500">Target</th>
                                    <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500 text-right">Fit</th>
                                    <th className="px-5 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-500 text-right">Projected revenue</th>
                                </tr>
                            </thead>
                            <tbody>
                                {stats.top_opportunities.map((s, i) => {
                                    const tone = s.fit_score >= 90 ? "text-emerald-400" : s.fit_score >= 80 ? "text-blue-400" : s.fit_score >= 70 ? "text-yellow-400" : "text-zinc-500";
                                    return (
                                        <tr key={`${s.firm_a_id}-${s.firm_b_id}-${s.product_id}`} className="border-b border-zinc-800/60 hover:bg-zinc-900/40" data-testid={`opportunity-row-${i}`}>
                                            <td className="px-5 py-3 font-mono text-xs text-zinc-600">{String(i + 1).padStart(2, "0")}</td>
                                            <td className="px-3 py-3">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    {s.pinned && <Pin className="w-3 h-3 text-amber-500 shrink-0" />}
                                                    {firmColor[s.firm_a_id] && <span className="w-2 h-2 rounded-full inline-block shrink-0" style={{ background: firmColor[s.firm_a_id] }} />}
                                                    <Link to={`/app/firms/${s.firm_a_id}`} className="text-zinc-100 hover:text-amber-400 truncate">{s.firm_a_name}</Link>
                                                </div>
                                                <div className="text-xs text-amber-500 font-mono mt-0.5 truncate">{s.product_name}</div>
                                            </td>
                                            <td className="px-3 py-3">
                                                <div className="flex items-center gap-2 min-w-0">
                                                    {firmColor[s.firm_b_id] && <span className="w-2 h-2 rounded-full inline-block shrink-0" style={{ background: firmColor[s.firm_b_id] }} />}
                                                    <Link to={`/app/firms/${s.firm_b_id}`} className="text-zinc-100 hover:text-amber-400 truncate">{s.firm_b_name}</Link>
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 text-right">
                                                <span className={`inline-flex items-center gap-1 font-mono text-sm ${tone}`}>
                                                    <Star className="w-3 h-3" style={{ fill: "currentColor" }} /> {s.fit_score}
                                                </span>
                                            </td>
                                            <td className="px-5 py-3 text-right font-mono text-sm text-amber-500">
                                                <Link
                                                    to={`/app/synergy?key=${encodeURIComponent(`${s.firm_a_id}:${s.firm_b_id}:${s.product_id}`)}`}
                                                    className="text-amber-500 hover:text-amber-300 underline-offset-2 hover:underline"
                                                    data-testid={`opportunity-reopen-${i}`}
                                                    title="Reopen synergy analysis"
                                                >
                                                    {fmtMoney(s.projected_revenue)}
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="border border-zinc-800 bg-zinc-950">
                    <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Top firms by revenue</div>
                        <div className="font-mono text-[10px] text-zinc-700">RANKED</div>
                    </div>
                    <ul>
                        {(stats?.top_firms || []).length === 0 && (
                            <li className="p-5 text-sm text-zinc-600">No firms yet. Run your first research →</li>
                        )}
                        {(stats?.top_firms || []).map((f, i) => (
                            <li key={f.id} className="flex items-center justify-between px-5 py-3 border-b border-zinc-800/60 last:border-b-0 hover:bg-zinc-900/40">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="font-mono text-xs text-zinc-600 w-6">{String(i + 1).padStart(2, "0")}</div>
                                    <Link to={`/app/firms/${f.id}`} className="text-sm text-zinc-100 hover:text-amber-500 truncate" data-testid={`top-firm-${i}`}>
                                        {f.name}
                                    </Link>
                                </div>
                                <div className="font-mono text-sm text-amber-500">{fmtMoney(f.revenue)}</div>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="border border-zinc-800 bg-zinc-950">
                    <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">Recent firms</div>
                        <div className="font-mono text-[10px] text-zinc-700">LATEST</div>
                    </div>
                    <ul>
                        {(stats?.recent_firms || []).length === 0 && (
                            <li className="p-5 text-sm text-zinc-600">Nothing here yet.</li>
                        )}
                        {(stats?.recent_firms || []).map((f) => (
                            <li key={f.id} className="px-5 py-3 border-b border-zinc-800/60 last:border-b-0 hover:bg-zinc-900/40">
                                <Link to={`/app/firms/${f.id}`} className="flex items-center justify-between" data-testid={`recent-firm-${f.id}`}>
                                    <div className="text-sm text-zinc-100 truncate">{f.name}</div>
                                    <div className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">{f.industry || "—"}</div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </div>
    );
}
